import React from "react";
import { hardRefreshToLatest } from "@/lib/app-version";
import { CHUNK_RETRY_FLAG } from "@/lib/lazy-retry";

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Patterns for errors that are almost always transient — a hook-count
 * mismatch (#310, #185, #418, #423), a React Suspense / hydration glitch,
 * or a chunk-load failure after a fresh deploy. The user-visible symptom
 * is a momentarily broken screen; reloading once almost always fixes it.
 *
 * If the SAME kind of error reoccurs in the same session after auto-reload,
 * we drop through to the visible error screen so the user can screenshot
 * + report rather than ending up in a reload loop.
 */
const TRANSIENT_PATTERNS: RegExp[] = [
  /Minified React error #(?:130|185|300|310|321|418|419|421|422|423|425|426|429)/i,
  /Rendered (?:more|fewer) hooks than/i,
  /Hydration failed/i,
  /Loading chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
];

const RELOAD_FLAG_KEY = "noknok.errorboundary.autoreload.v1";

function isTransient(error: Error): boolean {
  const msg = `${error.name ?? ""}: ${error.message ?? ""}\n${error.stack ?? ""}`;
  return TRANSIENT_PATTERNS.some((re) => re.test(msg));
}

function alreadyAutoReloadedThisSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

function markAutoReloaded() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
  } catch {
    /* noop */
  }
}

// Renders the caught error on-screen so crashes are visible on surfaces
// without devtools (iOS PWA, Android WebView). Desktop debug still uses the
// browser console; this is the screenshottable fallback for mobile.
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    console.error("[ErrorBoundary]", error, info);

    // Auto-recover from transient React errors. The user sees a brief flash
    // and the page reloads instead of a broken-screen they have to refresh
    // manually. Gated on a sessionStorage flag so we don't loop if the
    // error reproduces immediately after reload — in that case we fall
    // through to the visible error screen.
    if (isTransient(error) && !alreadyAutoReloadedThisSession()) {
      markAutoReloaded();
      console.warn(
        "[ErrorBoundary] Transient error detected, hard-refreshing once.",
        error.message,
      );
      // Hard refresh (not plain reload): SW precache can re-serve a stale shell
      // after deploy, which re-breaks on the same missing chunk. Unregister +
      // clear caches first so the next navigation hits the live index.html.
      setTimeout(() => {
        void hardRefreshToLatest();
      }, 0);
    }
  }

  private reset = () => {
    this.setState({ error: null, componentStack: null });
  };

  private hardReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
      sessionStorage.removeItem(CHUNK_RETRY_FLAG);
    } catch { /* noop */ }
    void hardRefreshToLatest();
  };

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    // If we're about to auto-reload, render a minimal "reloading" placeholder
    // instead of the scary error UI — the reload fires from componentDidCatch
    // and is essentially instantaneous.
    if (isTransient(error) && alreadyAutoReloadedThisSession() === false) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            color: "#666",
            fontFamily: "system-ui, sans-serif",
            fontSize: 14,
          }}
        >
          Reloading…
        </div>
      );
    }

    const reportLines = [
      `Message: ${error.message}`,
      error.stack ? `\nStack:\n${error.stack}` : "",
      componentStack ? `\nComponent:\n${componentStack}` : "",
      `\nURL: ${typeof window !== "undefined" ? window.location.href : ""}`,
      `UA: ${typeof navigator !== "undefined" ? navigator.userAgent : ""}`,
    ].join("");

    return (
      <div
        style={{
          minHeight: "100vh",
          padding: "20px 16px calc(20px + env(safe-area-inset-bottom))",
          background: "#fff",
          color: "#111",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 13,
          lineHeight: 1.45,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h1 style={{ fontSize: 18, margin: "0 0 8px", color: "#b91c1c" }}>
            Something broke
          </h1>
          <p style={{ margin: "0 0 16px", color: "#555", fontFamily: "system-ui, sans-serif" }}>
            Screenshot this and send it over. Tap Retry to try again.
          </p>

          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#f6f8fa",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              padding: 12,
              margin: 0,
              userSelect: "text",
              WebkitUserSelect: "text",
            }}
          >
            {reportLines}
          </pre>

          <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={this.hardReload}
              style={{
                padding: "10px 16px",
                borderRadius: 6,
                border: "1px solid #111",
                background: "#111",
                color: "#fff",
                fontSize: 14,
                fontFamily: "system-ui, sans-serif",
                cursor: "pointer",
              }}
            >
              Reload latest
            </button>
            <button
              onClick={this.reset}
              style={{
                padding: "10px 16px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: "#fff",
                fontSize: 14,
                fontFamily: "system-ui, sans-serif",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
}
